import { Group } from "./group";
import { Project } from "./project";
import { Tracker } from "./tracker";
import { User } from "./user";
import { Zone } from "./zone";

export const defaultUnit: Unit = {
    id: "",
    trackers: [],
    zones: "",
    groups: [],
    projects: [],
    type: 'worker',
    name: "",
    location: "",
    status: 0,
    picture: 'worker',
    collider: {
        visible: true,
        geometry: 'Sphere',
        bottomradius: 1,
        topradius: 1,
        width: 1,
        height: 1,
        length: 1,
        radius: 1,
        offset: {
            x: 0,
            y: 0,
            z: 0
        },
        extrapolationCoef: 5,
        hookMargin: 0
    },
    crane: {
        type: "tower",
        unloadedWeight: 150,
        fromTracker: false,
        trackerOnTop: false,
        fullHeight: false,
        offsetAngle: 0,
        offsetAxis: "z"
    },
    startDate: Date.now(),
    createdDate: Date.now(),
    costByUnit: 0,
    unitOfMeasure: "",
    endDate: "",
    editedBy: "",
    data: {},
    lastPosition: {
        x: 0,
        y: 0,
        z: 0
    },
    schedule: {
        mon: [],
        tue: [],
        wed: [],
        thu: [],
        fri: [],
        sat: [],
        sun: []
    },
    visible: true,
    trace: false,
    distance: 0,
    description: "",
    tags: [],
    color: "primary",
    alpha: 0.5,
    lastUpdate: Date.now()
}

export interface Unit {
    [x: string]: any;
    id: string;
    trackers: (Tracker | string)[];
    trackerIds?: string[];
    zones?: Zone | string;
    groups?: (Group | string)[];
    projects?: (Project | string)[];
    type?: 'worker' | 'equipment' | 'material' | 'virtual' | 'camera';
    name: string;
    location?: string;
    status: string | number; // default: 0
    picture: string; // default: 'worker'
    collider: {
        visible?: boolean; // default: true
        geometry: string; // default: 'Sphere'
        bottomradius?: number; // default: 1
        topradius?: number; // default: 1
        width?: number; // default: 1
        height?: number; // default: 1
        length?: number; // default: 1
        radius?: number; // default: 1
        offset?: {
            x: number; // default: 0
            y: number; // default: 0
            z: number; // default: 0
        };
        extrapolationCoef?: number; // default: 5
        hookMargin?: number; // default: 0
    };
    crane?: {
        type: string; // default: "tower"
        height?: number;
        width?: number;
        unloadedWeight: number; // default: 150
        jib_length?: number;
        counter_jib_length?: number;
        jibHeight?: number;
        jibLength?: number;
        counterJibLength?: number;
        hookTracker?: Tracker | string;
        jibTracker?: Tracker | string;
        fromTracker: boolean; // default: false
        trackerOnTop: boolean; // default: false
        fullHeight: boolean; // default: false
        hook?: any[];
        jib?: any[];
        tower?: any[];
        trolley?: any[];
        offsetAngle: number; // default: 0
        offsetAxis: string; // default: "z"
    };
    startDate: string | number; // default: Date.now()
    createdDate: string | number; // default: Date.now()
    costByUnit?: number;
    unitOfMeasure?: string;
    endDate?: string | number;
    editedBy?: User | string;
    data: Record<string, any>; // default: {}
    lastPosition?: {
        lat?: number;
        lon?: number;
        x: number; // default: 0
        y: number; // default: 0
        z: number; // default: 0
    };
    offset?: {
        x: number; // default: 0
        y: number; // default: 0
        z: number; // default: 0
    };
    schedule?: {
        mon?: any[];
        tue?: any[];
        wed?: any[];
        thu?: any[];
        fri?: any[];
        sat?: any[];
        sun?: any[];
    };
    visible: boolean; // default: true
    trace: boolean; // default: false
    distance: number; // default: 0
    description: string; // default: ""
    tags?: string[];
    color: string; // default: "primary"
    alpha: number; // default: 0.5
    lastUpdate: string | number; // default: Date.now()
    colorName?: string;
    icon?: string;
}

export interface PostUnit {
    trackers?: (Tracker | string)[];
    zones?: Zone | string;
    groups?: (Group | string)[];
    projects?: (Project | string)[];
    type?: 'worker' | 'equipment' | 'material' | 'virtual' | 'camera';
    name: string;
    location?: string;
    status?: string | number; // default: 0
    picture?: string; // default: 'worker'
    collider?: {
        visible?: boolean; // default: true
        geometry?: string; // default: 'Sphere'
        bottomradius?: number; // default: 1
        topradius?: number; // default: 1
        width?: number; // default: 1
        height?: number; // default: 1
        length?: number; // default: 1
        radius?: number; // default: 1
        offset?: {
            x?: number; // default: 0
            y?: number; // default: 0
            z?: number; // default: 0
        };
        extrapolationCoef?: number; // default: 5
        hookMargin?: number; // default: 0
    };
    crane?: {
        type?: string; // default: "tower"
        height?: number;
        width?: number;
        unloadedWeight?: number; // default: 150
        jib_length?: number;
        counter_jib_length?: number;
        jibHeight?: number;
        jibLength?: number;
        counterJibLength?: number;
        hookTracker?: Tracker | string;
        jibTracker?: Tracker | string;
        fromTracker?: boolean; // default: false
        trackerOnTop?: boolean; // default: false
        fullHeight?: boolean; // default: false
        hook?: any[];
        jib?: any[];
        tower?: any[];
        trolley?: any[];
        offsetAngle?: number; // default: 0
        offsetAxis?: string; // default: "z"
    };
    startDate?: string | number; // default: Date.now()
    createdDate?: string | number; // default: Date.now()
    costByUnit?: number;
    unitOfMeasure?: string;
    endDate?: string | number;
    editedBy?: User | string;
    data?: Record<string, any>; // default: {}
    lastPosition?: {
        lat?: number;
        lon?: number;
        x?: number; // default: 0
        y?: number; // default: 0
        z?: number; // default: 0
    };
    schedule?: {
        mon?: any[];
        tue?: any[];
        wed?: any[];
        thu?: any[];
        fri?: any[];
        sat?: any[];
        sun?: any[];
    };
    visible?: boolean; // default: true
    trace?: boolean; // default: false
    distance?: number; // default: 0
    description?: string; // default: ""
    tags?: string[];
    color?: string; // default: "primary"
    alpha?: number; // default: 0.5
    lastUpdate?: string | number; // default: Date.now()
}

export interface PutUnit {
    id: string; // Obligatoire pour identifier l'unité à mettre à jour
    trackers?: (Tracker | string)[];
    zones?: Zone | string;
    groups?: (Group | string)[];
    projects?: (Project | string)[];
    type?: 'worker' | 'equipment' | 'material' | 'virtual' | 'camera';
    name?: string;
    location?: string;
    status?: string | number;
    picture?: string;
    collider?: {
        visible?: boolean;
        geometry?: string;
        bottomradius?: number;
        topradius?: number;
        width?: number;
        height?: number;
        length?: number;
        radius?: number;
        offset?: {
            x?: number;
            y?: number;
            z?: number;
        };
        extrapolationCoef?: number;
        hookMargin?: number;
    };
    crane?: {
        type?: string;
        height?: number;
        width?: number;
        unloadedWeight?: number;
        jib_length?: number;
        counter_jib_length?: number;
        jibHeight?: number;
        jibLength?: number;
        counterJibLength?: number;
        hookTracker?: Tracker | string;
        jibTracker?: Tracker | string;
        fromTracker?: boolean;
        trackerOnTop?: boolean;
        fullHeight?: boolean;
        hook?: any[];
        jib?: any[];
        tower?: any[];
        trolley?: any[];
        offsetAngle?: number;
        offsetAxis?: string;
    };
    startDate?: string | number;
    createdDate?: string | number;
    costByUnit?: number;
    unitOfMeasure?: string;
    endDate?: string | number;
    editedBy?: User | string;
    data?: Record<string, any>;
    lastPosition?: {
        lat?: number;
        lon?: number;
        x?: number;
        y?: number;
        z?: number;
    };
    schedule?: {
        mon?: any[];
        tue?: any[];
        wed?: any[];
        thu?: any[];
        fri?: any[];
        sat?: any[];
        sun?: any[];
    };
    visible?: boolean;
    trace?: boolean;
    distance?: number;
    description?: string;
    tags?: string[];
    color?: string;
    alpha?: number;
    lastUpdate?: string | number;
}
